import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { Subject } from "rxjs";
import { environment } from "../../../environments/environment";
import { ApiCacheService } from "./api-cache.service";

@Injectable({
  providedIn: "root",
})
export class SignalRService {
  // signals
  roleChanged = signal<boolean>(false);
  notifications = signal<
    { isAllowed: boolean; chatId: number; moduleName: string } | false
  >(false);
  messageList = signal<boolean>(false);
  updates = signal<boolean>(false);
  trackingBarChanges = signal<
    | { isAllowed: boolean; data: { projectId: number; saleOrderId: string } }
    | false
  >(false);

  /**
   * Emits when the server requests a cache flush.
   * `null`  → invalidate everything
   * `string` → invalidate that specific API URL
   */
  readonly cacheInvalidate$ = new Subject<string | null>();
  private hubConnection!: HubConnection;
  private userManagementBaseURL = environment.baseUrl + "/User";

  constructor(
    private _http: HttpClient,
    private _apiCache: ApiCacheService
  ) {}

  public createConnection() {
    if (localStorage.hasOwnProperty("user")) {
      this.getConnectionInfo().subscribe(
        (info: { accessToken: string; url: string }) => {
          const options = {
            accessTokenFactory: () => info.accessToken,
          };

          this.hubConnection = new HubConnectionBuilder()
            .withUrl(info.url, options)
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();
          this.register();
          this.startConnection();
        }
      );
    }
  }

  getConnectionInfo() {
    let userObject;
    const userJson = localStorage.getItem("user");
    if (userJson) {
      userObject = JSON.parse(userJson);
    }

    return this._http.get<{ accessToken: string; url: string }>(
      `${this.userManagementBaseURL}/NegotiateAsync`,
      {
        headers: new HttpHeaders().set(
          "x-application-user-id",
          userObject.idTokenClaims.preferred_username
        ),
      }
    );
  }

  private register() {
    this.hubConnection.onclose(() => {
      //console.log('disconnected');
    }); // call event handlers here
    this.hubConnection.on("UserProfileUpdate", () => {
      this.roleChanged.set(true);
    });
    this.hubConnection.on("TargetGetUserNotifications", params => {
      const data = JSON.parse(params) as { chatId: string; moduleName: string };
      const request = {
        isAllowed: true,
        chatId: Number(data.chatId),
        moduleName: data.moduleName,
      };
      //console.log('target Notification List');
      this.notifications.set(request);
    });
    this.hubConnection.on("TargetGetNewChatMessages", () => {
      //console.log('target Meassage List');
      this.messageList.set(true);
    });
    this.hubConnection.on("TargetGetProductionWorkflowUpdates", () => {
      //console.log('target production worflow updates');
      this.updates.set(true);
    });
    this.hubConnection.on("TargetUpdateTrackingProgressBar", params => {
      const request = {
        isAllowed: true,
        data: JSON.parse(params) as { projectId: number; saleOrderId: string },
      };
      //console.log('target Tracking Bar Changes List');
      this.trackingBarChanges.set(request);
    });

    /**
     * Payload: JSON string with shape { key?: string }
     *   key absent / null → flush entire cache
     *   key present      → flush only that API URL
     */
    this.hubConnection.on("CacheInvalidate", (params?: string) => {
      try {
        const payload = params ? (JSON.parse(params) as { key?: string }) : {};
        const key = payload.key ?? null;
        this.cacheInvalidate$.next(key);
        if (key) {
          this._apiCache.invalidate(key);
        } else {
          this._apiCache.invalidateAll();
        }
      } catch {
        this.cacheInvalidate$.next(null);
        this._apiCache.invalidateAll();
      }
    });
  }

  private startConnection() {
    this.hubConnection
      .start()
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .then(() => {})
      .catch();
  }
}
